'use client'

import { Children, isValidElement, type ReactElement, type ReactNode } from 'react'
import { CodeBlock } from './code-block'

interface CodeProps {
  className?: string
  children?: ReactNode
}

/**
 * Extract language from className (e.g., "language-typescript" → "typescript")
 * Also handles Shiki's theme classes (e.g., "shiki shiki-themes ... language-typescript")
 */
function getLanguageFromClassName(className?: string): string {
  if (!className) return 'typescript'
  const match = className.match(/language-(\w+)/)
  if (!match) return 'typescript'

  const lang = match[1].toLowerCase()

  // Map common aliases to valid Shiki languages
  const languageMap: Record<string, string> = {
    jsx: 'javascript',
    js: 'javascript',
    ts: 'typescript',
    tsx: 'typescript',
    py: 'python',
    sh: 'bash',
    yml: 'yaml',
  }

  return languageMap[lang] || lang
}

/**
 * Extract text content from React children recursively
 * Handles both plain text and Shiki-highlighted spans
 */
function extractTextContent(children: ReactNode): string {
  if (typeof children === 'string') return children
  if (typeof children === 'number') return String(children)
  if (!children) return ''

  if (Array.isArray(children)) {
    return children.map(extractTextContent).join('')
  }

  if (isValidElement(children)) {
    return extractTextContent((children as ReactElement<{ children?: ReactNode }>).props.children)
  }

  return ''
}

export interface MdxPreProps {
  children?: ReactNode
  className?: string
}

/**
 * MDX Pre component override
 *
 * This component intercepts MDX's `<pre><code>` output and renders
 * it using our styled CodeBlock component with syntax highlighting,
 * including copy button and proper styling.
 *
 * @example
 * ```tsx
 * // In MDX component registry:
 * const components = {
 *   pre: MdxPre,
 * }
 *
 * <Content components={components} />
 * ```
 */
export function MdxPre({ children, className }: MdxPreProps) {
  // MDX renders: <pre><code className="language-xxx">...</code></pre>
  // We need to extract the code element and its properties

  // Find the code element in children
  const codeElement = Children.toArray(children).find(
    (child): child is ReactElement<CodeProps> =>
      isValidElement(child) && (child.type === 'code' || (child.type as { displayName?: string })?.displayName === 'code')
  )

  if (!codeElement) {
    // Fallback: if no code element, render as-is
    return <pre className={className}>{children}</pre>
  }

  const codeProps = codeElement.props
  // Check both the pre className and code className for language
  const language = getLanguageFromClassName(codeProps.className) || getLanguageFromClassName(className)
  const code = extractTextContent(codeProps.children)

  // Always use CodeBlock for consistent styling and copy button
  return <CodeBlock language={language}>{code}</CodeBlock>
}
